'use client';

import React, { useRef, useEffect, useState } from 'react';

interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  event: string;
  avatar: string;
  avatarAlt: string;
}

const testimonials: Testimonial[] = [
{
  id: 1,
  quote: "Nous sommes venus en famille pour le buffet du dimanche. Les enfants ont adoré les nems et nous avons découvert des saveurs authentiques. Le rapport qualité-prix est excellent. Nous reviendrons certainement.",
  name: 'Marie Dupont',
  role: 'Cliente régulière',
  event: 'Ajaccio, Mars 2025',
  avatar: "/assets/images/avatar-1.png",
  avatarAlt: 'Marie Dupont, cliente avec sourire'
},
{
  id: 2,
  quote: "Notre équipe de 30 personnes a passé un excellent moment. Le service est impeccable, les plats sont variés et tout est frais. CorsAsia est devenu notre adresse préférée pour les déjeuners d\'entreprise.",
  name: 'Pierre Martin',
  role: 'Directeur RH',
  event: 'Entreprise Corse Technologies, Bastia',
  avatar: "/assets/images/avatar-2.png",
  avatarAlt: 'Pierre Martin, directeur avec costume'
},
{
  id: 3,
  quote: "Pour notre mariage, nous avons choisi CorsAsia pour le repas. Nos invités étaient ravis ! Le buffet a permis à chacun de trouver son bonheur. L\'équipe a été professionnelle et à l\'écoute.",
  name: 'Sophie & Luc Bernard',
  role: 'Mariage',
  event: 'Domaine de Murtoli, Juin 2025',
  avatar: "/assets/images/avatar-3.png",
  avatarAlt: 'Sophie Bernard, mariée souriante'
},
{
  id: 4,
  quote: "En tant qu\'amateur de cuisine asiatique authentique, j\'ai été bluffé par la qualité des plats. Les saveurs sont vraies, les ingrédients frais. C\'est comme être transporté en Asie le temps d\'un repas.",
  name: 'Antoine Rossi',
  role: 'Critique gastronomique',
  event: 'Guide restaurants Corses, 2025',
  avatar: "/assets/images/avatar-4.png",
  avatarAlt: 'Antoine Rossi, critique culinaire'
}];


const TestimonialsSection: React.FC = () => {
  const railRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll('.reveal-on-scroll');
            reveals.forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const updateScrollState = () => {
    if (!railRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = railRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (dir: 'left' | 'right') => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: dir === 'right' ? 560 : -560, behavior: 'smooth' });
  };

  return (
    <section
      id="events"
      ref={sectionRef}
      className="py-32 bg-charcoal-mid relative overflow-hidden">
      
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-corsasia/5 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-clay text-xs uppercase tracking-widest font-sans font-medium mb-4">
              Avis Clients
            </p>
            <h2
              className="font-serif italic text-parchment leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>

              Ce qu'ils disent<br />
              <span className="text-clay">de nous.</span>
            </h2>
          </div>

          {/* Scroll controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll('left')}
              aria-label="Témoignage précédent"
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300 ${
              canScrollLeft ?
              'border-clay/40 text-parchment hover:bg-clay/10 hover:border-clay' : 'border-charcoal-light text-parchment/20 cursor-not-allowed'}`
              }>

              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              aria-label="Témoignage suivant"
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300 ${
              canScrollRight ?
              'border-clay/40 text-parchment hover:bg-clay/10 hover:border-clay' : 'border-charcoal-light text-parchment/20 cursor-not-allowed'}`
              }>
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Testimonial Rail */}
      <div className="relative">
        {/* Fade masks */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-charcoal-mid to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-charcoal-mid to-transparent z-10 pointer-events-none" />

        <div
          ref={railRef}
          onScroll={updateScrollState}
          className="flex gap-6 overflow-x-auto px-6 lg:px-16 pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          
          {testimonials.map((t, i) =>
          <article
            key={t.id}
            className="testimonial-card shrink-0 w-[420px] sm:w-[500px] bg-parchment rounded-4xl p-8 shadow-2xl shadow-charcoal/30 cursor-default transition-all duration-500"
            style={{ transitionTimingFunction: 'cubic-bezier(0.25, 1, 0.5, 1)' }}>
            
              {/* Quote mark */}
              <div className="font-serif italic text-6xl text-clay/30 leading-none mb-4 select-none">"</div>
              <p className="font-serif italic text-charcoal text-xl leading-relaxed mb-8">
                {t.quote}
              </p>
              <div className="flex items-center gap-4 pt-6 border-t border-clay/20">
                <img
                src={t.avatar}
                alt={t.avatarAlt}
                className="w-12 h-12 rounded-full object-cover border-2 border-clay/20" />
              
                <div>
                  <p className="font-sans font-semibold text-charcoal text-sm">{t.name}</p>
                  <p className="font-sans text-charcoal/50 text-xs">{t.role}</p>
                  <p className="font-sans text-clay text-xs italic mt-0.5">{t.event}</p>
                </div>
              </div>
            </article>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="reveal-on-scroll max-w-7xl mx-auto px-6 mt-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-parchment/10 rounded-3xl overflow-hidden border border-parchment/10">
          {[
          { num: '15k+', label: 'Clients Satisfaits' },
          { num: '4.8/5', label: 'Note Moyenne' },
          { num: '95%', label: 'Recommandation' },
          { num: '7j/7', label: 'Service Ouvert' }].
          map((stat) =>
          <div key={stat.num} className="bg-charcoal-light/30 p-8 text-center hover:bg-charcoal-light/50 transition-colors">
              <p className="font-serif italic text-parchment text-4xl mb-2">{stat.num}</p>
              <p className="font-sans text-parchment/40 text-xs uppercase tracking-widest">{stat.label}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;